// AUTHORITATIVE DEVICE CATALOG - DO NOT AUTO-GENERATE OR SIMPLIFY
// All base prices in Chinese Yuan (CNY) — converted via exchange rate
// Samsung/non-Apple devices: prices stored as GHS equivalent base

import { isTenantContext } from "@/lib/tenantScope";


export type DeviceCategory = "iphone" | "samsung" | "apple-watch" | "samsung-watch" | "pixel-watch" | "ipad" | "android-tablet" | string;

export type VariantConfigType = "storage" | "size" | "storage_ram" | "none";

export interface StorageVariant {
  label: string;
  price: number;
}

export interface DeviceModel {
  name: string;
  category: DeviceCategory;
  brand: string;
  variants: StorageVariant[];
  usesYuanPricing: boolean;
}

export type Grade = "Grade A" | "Grade B+" | "Grade B";
export const grades: Grade[] = ["Grade A", "Grade B+", "Grade B"];

export type ScreenCondition = "Original Screen" | "3rd Party Screen";
export const screenConditions: ScreenCondition[] = ["Original Screen", "3rd Party Screen"];

export type DisplayCurrency = "GHS" | "USD" | "CNY";



// Custom brands store
const CUSTOM_BRANDS_KEY = "khays_custom_brands";

export interface CustomBrand {
  id: string;
  name: string;
  variantConfig: VariantConfigType;
  categories: string[]; // category keys this brand maps to
}

export function loadCustomBrands(): CustomBrand[] {
  try { const s = localStorage.getItem(CUSTOM_BRANDS_KEY); if (s) return JSON.parse(s); } catch {} return [];
}
export function saveCustomBrands(brands: CustomBrand[]): void {
  localStorage.setItem(CUSTOM_BRANDS_KEY, JSON.stringify(brands));
}

// Custom categories store
const CUSTOM_CATEGORIES_KEY = "khays_custom_categories";

export interface CustomCategory {
  id: string; // used as DeviceCategory key
  label: string;
  brand: string;
  variantConfig: VariantConfigType;
}

export function loadCustomCategories(): CustomCategory[] {
  try { const s = localStorage.getItem(CUSTOM_CATEGORIES_KEY); if (s) return JSON.parse(s); } catch {} return [];
}
export function saveCustomCategories(cats: CustomCategory[]): void {
  localStorage.setItem(CUSTOM_CATEGORIES_KEY, JSON.stringify(cats));
}

// Built-in brand list
export const BUILTIN_BRANDS = ["Apple", "Samsung", "Google", "Accessories"];

// Deleted built-in brands/categories store
const DELETED_BUILTIN_BRANDS_KEY = "khays_deleted_builtin_brands";
const DELETED_BUILTIN_CATEGORIES_KEY = "khays_deleted_builtin_categories";

export function loadDeletedBuiltinBrands(): string[] {
  try { const s = localStorage.getItem(DELETED_BUILTIN_BRANDS_KEY); if (s) return JSON.parse(s); } catch {} return [];
}
export function saveDeletedBuiltinBrands(brands: string[]): void {
  localStorage.setItem(DELETED_BUILTIN_BRANDS_KEY, JSON.stringify(brands));
}
export function loadDeletedBuiltinCategories(): string[] {
  try { const s = localStorage.getItem(DELETED_BUILTIN_CATEGORIES_KEY); if (s) return JSON.parse(s); } catch {} return [];
}
export function saveDeletedBuiltinCategories(cats: string[]): void {
  localStorage.setItem(DELETED_BUILTIN_CATEGORIES_KEY, JSON.stringify(cats));
}

export function getAllBrands(): string[] {
  // Tenants start empty — do not surface platform built-in brands until
  // the Store Manager imports the starter catalog or adds custom brands.
  const tenantScoped = isTenantContext();
  const deleted = loadDeletedBuiltinBrands();
  const active = tenantScoped ? [] : BUILTIN_BRANDS.filter(b => !deleted.includes(b));
  const custom = loadCustomBrands().map(b => b.name);
  return [...active, ...custom.filter(b => !active.includes(b))];
}

export function getDeletedBuiltinBrandsWithCategories(): { brand: string; categories: string[] }[] {
  const deleted = loadDeletedBuiltinBrands();
  const brandCategoryMap: Record<string, string[]> = {
    Apple: ["iphone", "apple-watch", "ipad"],
    Samsung: ["samsung", "samsung-watch", "android-tablet"],
    Google: ["pixel-watch"],
    Accessories: ["accessories"],
  };
  return deleted.map(b => ({ brand: b, categories: brandCategoryMap[b] || [] }));
}

export function isAppleDevice(category: DeviceCategory): boolean {
  return category === "iphone" || category === "apple-watch" || category === "ipad";
}

export function isWatchCategory(category: DeviceCategory): boolean {
  return category === "apple-watch" || category === "samsung-watch" || category === "pixel-watch";
}

export function isSamsungDevice(category: DeviceCategory): boolean {
  return category === "samsung" || category === "android-tablet";
}

export function getVariantConfig(category: DeviceCategory): VariantConfigType {
  if (isWatchCategory(category)) return "size";
  if (isSamsungDevice(category)) return "storage_ram";
  if (category === "accessories") return "none";
  const customCats = loadCustomCategories();
  const cc = customCats.find(c => c.id === category);
  if (cc) return cc.variantConfig;
  return "storage";
}

export function getStorageLabel(category: DeviceCategory): string {
  const config = getVariantConfig(category);
  if (config === "none") return "Variant";
  if (config === "size") return "Size";
  if (config === "storage_ram") return "Storage & RAM";
  return "Storage";
}

const defaultCategoryLabels: Record<string, string> = {
  iphone: "iPhones",
  samsung: "Samsung Galaxy",
  "apple-watch": "Apple Watch",
  "samsung-watch": "Samsung Watch",
  "pixel-watch": "Pixel Watch",
  ipad: "iPads",
  "android-tablet": "Android Tablets",
  accessories: "Accessories",
};

export function getCategoryLabels(): Record<string, string> {
  const custom = loadCustomCategories();
  const deletedCats = loadDeletedBuiltinCategories();
  const overrides = loadCategoryLabelOverrides();
  const labels: Record<string, string> = {};
  // Tenants start empty: do not surface platform built-in categories.
  if (!isTenantContext()) {
    for (const [k, v] of Object.entries(defaultCategoryLabels)) {
      if (!deletedCats.includes(k)) labels[k] = overrides[k] || v;
    }
  }
  custom.forEach(c => { labels[c.id] = overrides[c.id] || c.label; });
  return labels;
}

const CATEGORY_ORDER_KEY = "khays_category_order";

export function loadCategoryOrderOverride(): string[] {
  try { const s = localStorage.getItem(CATEGORY_ORDER_KEY); if (s) return JSON.parse(s); } catch {}
  return [];
}
export function saveCategoryOrderOverride(order: string[]): void {
  localStorage.setItem(CATEGORY_ORDER_KEY, JSON.stringify(order));
}

export function getCategoryOrder(): string[] {
  const custom = loadCustomCategories();
  const deletedCats = loadDeletedBuiltinCategories();
  // Tenants start empty: skip platform built-in categories entirely.
  const builtinPart = isTenantContext()
    ? []
    : defaultCategoryOrder.filter(c => !deletedCats.includes(c));
  const base = [...builtinPart, ...custom.map(c => c.id)];
  const override = loadCategoryOrderOverride();
  if (!override.length) return base;
  const inBase = new Set(base);
  const ordered = override.filter(c => inBase.has(c));
  const remaining = base.filter(c => !ordered.includes(c));
  return [...ordered, ...remaining];
}

export function moveCategoryInOrder(catId: string, dir: "up" | "down"): void {
  const current = getCategoryOrder();
  const idx = current.indexOf(catId);
  if (idx < 0) return;
  const swap = dir === "up" ? idx - 1 : idx + 1;
  if (swap < 0 || swap >= current.length) return;
  const next = [...current];
  [next[idx], next[swap]] = [next[swap], next[idx]];
  saveCategoryOrderOverride(next);
}

// Keep backward compat export
export const categoryLabels = defaultCategoryLabels;

const defaultCategoryOrder: string[] = [
  "iphone", "samsung", "apple-watch", "samsung-watch", "pixel-watch", "ipad", "android-tablet", "accessories",
];

// getCategoryOrder moved above with deleted categories filtering

export const categoryOrder = defaultCategoryOrder;

export const currencySymbols: Record<DisplayCurrency, string> = {
  GHS: "GH₵",
  USD: "$",
  CNY: "¥",
};

export function formatPrice(price: number, currency: DisplayCurrency = "GHS"): string {
  const symbol = currencySymbols[currency];
  return `${symbol}${price.toLocaleString("en-GH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function calculateFinalPrice(
  basePrice: number,
  marginPercent: number,
  exchangeRate: number,
  usesYuanPricing: boolean,
  manualExtraProfit: number = 0
): number {
  if (basePrice <= 0) return 0;
  const a1 = basePrice;
  const b1 = a1 * marginPercent;
  const c1 = a1 + b1;
  let converted: number;
  if (usesYuanPricing) {
    converted = Math.round(c1 * exchangeRate);
  } else {
    converted = Math.round(c1);
  }
  return converted + manualExtraProfit;
}

export function convertFromGHS(
  ghsPrice: number,
  displayCurrency: DisplayCurrency,
  usdToGhsRate: number,
  cnyToGhsRate: number
): number {
  if (displayCurrency === "GHS") return ghsPrice;
  if (displayCurrency === "USD") {
    return usdToGhsRate > 0 ? Math.round((ghsPrice / usdToGhsRate) * 100) / 100 : 0;
  }
  if (displayCurrency === "CNY") {
    return cnyToGhsRate > 0 ? Math.round((ghsPrice / cnyToGhsRate) * 100) / 100 : 0;
  }
  return ghsPrice;
}

export function getPriceKey(
  deviceName: string,
  variantLabel: string,
  grade: string,
  screenCondition?: ScreenCondition
): string {
  const parts = [deviceName, variantLabel, grade];
  if (screenCondition) parts.push(screenCondition);
  return parts.join("|");
}

// Extra variants store (for adding variants to catalog devices)
const EXTRA_VARIANTS_KEY = "khays_extra_variants";
export function loadExtraVariants(): Record<string, StorageVariant[]> {
  try { const s = localStorage.getItem(EXTRA_VARIANTS_KEY); if (s) return JSON.parse(s); } catch {} return {};
}
export function saveExtraVariants(data: Record<string, StorageVariant[]>): void {
  localStorage.setItem(EXTRA_VARIANTS_KEY, JSON.stringify(data));
}
export function addExtraVariant(deviceName: string, variant: StorageVariant): void {
  const all = loadExtraVariants();
  if (!all[deviceName]) all[deviceName] = [];
  all[deviceName].push(variant);
  saveExtraVariants(all);
}
export function removeExtraVariant(deviceName: string, idx: number): void {
  const all = loadExtraVariants();
  if (!all[deviceName]) return;
  all[deviceName].splice(idx, 1);
  if (all[deviceName].length === 0) delete all[deviceName];
  saveExtraVariants(all);
}
export function getDeviceVariants(device: DeviceModel): StorageVariant[] {
  const extra = loadExtraVariants()[device.name] || [];
  const overrides = loadVariantOverrides()[device.name] || {};
  return [...device.variants, ...extra]
    .map(v => {
      const ov = overrides[v.label];
      if (!ov) return v;
      // price -1 means variant is hidden/removed
      if (ov.price === -1) return null;
      return { label: ov.label ?? v.label, price: ov.price ?? v.price };
    })
    .filter((v): v is StorageVariant => v !== null);
}

/** Get all variants including hidden ones (for admin DeviceManager) */
export function getDeviceVariantsIncludeHidden(device: DeviceModel): StorageVariant[] {
  const extra = loadExtraVariants()[device.name] || [];
  const overrides = loadVariantOverrides()[device.name] || {};
  return [...device.variants, ...extra].map(v => {
    const ov = overrides[v.label];
    if (!ov) return v;
    return { label: ov.label ?? v.label, price: ov.price ?? v.price };
  });
}

// ===== Variant Overrides Store =====
const VARIANT_OVERRIDES_KEY = "khays_variant_overrides";

export interface VariantOverride {
  label?: string;
  price?: number;
}

export function loadVariantOverrides(): Record<string, Record<string, VariantOverride>> {
  try { const s = localStorage.getItem(VARIANT_OVERRIDES_KEY); if (s) return JSON.parse(s); } catch {} return {};
}
export function saveVariantOverrides(data: Record<string, Record<string, VariantOverride>>): void {
  localStorage.setItem(VARIANT_OVERRIDES_KEY, JSON.stringify(data));
}
export function setVariantOverride(deviceName: string, originalLabel: string, override: VariantOverride): void {
  const all = loadVariantOverrides();
  if (!all[deviceName]) all[deviceName] = {};
  all[deviceName][originalLabel] = override;
  saveVariantOverrides(all);
}
export function removeVariantOverride(deviceName: string, originalLabel: string): void {
  const all = loadVariantOverrides();
  if (all[deviceName]) {
    delete all[deviceName][originalLabel];
    if (Object.keys(all[deviceName]).length === 0) delete all[deviceName];
  }
  saveVariantOverrides(all);
}

// ===== Device & Brand/Category Rename Stores =====
const DEVICE_RENAMES_KEY = "khays_device_renames";
const BRAND_RENAMES_KEY = "khays_brand_renames";
const CATEGORY_RENAMES_KEY = "khays_category_renames";

export function loadDeviceRenames(): Record<string, string> {
  try { const s = localStorage.getItem(DEVICE_RENAMES_KEY); if (s) return JSON.parse(s); } catch {} return {};
}
export function saveDeviceRenames(renames: Record<string, string>): void {
  localStorage.setItem(DEVICE_RENAMES_KEY, JSON.stringify(renames));
}
export function getDeviceDisplayName(deviceName: string): string {
  return loadDeviceRenames()[deviceName] || deviceName;
}
export function setDeviceDisplayName(deviceName: string, displayName: string): void {
  const renames = loadDeviceRenames();
  if (displayName && displayName !== deviceName) renames[deviceName] = displayName;
  else delete renames[deviceName];
  saveDeviceRenames(renames);
}

export function loadBrandRenames(): Record<string, string> {
  try { const s = localStorage.getItem(BRAND_RENAMES_KEY); if (s) return JSON.parse(s); } catch {} return {};
}
export function saveBrandRenames(renames: Record<string, string>): void {
  localStorage.setItem(BRAND_RENAMES_KEY, JSON.stringify(renames));
}
export function getBrandDisplayName(brand: string): string {
  return loadBrandRenames()[brand] || brand;
}
export function setBrandDisplayName(brand: string, displayName: string): void {
  const renames = loadBrandRenames();
  if (displayName && displayName !== brand) renames[brand] = displayName;
  else delete renames[brand];
  saveBrandRenames(renames);
}

export function loadCategoryLabelOverrides(): Record<string, string> {
  try { const s = localStorage.getItem(CATEGORY_RENAMES_KEY); if (s) return JSON.parse(s); } catch {} return {};
}
export function saveCategoryLabelOverrides(overrides: Record<string, string>): void {
  localStorage.setItem(CATEGORY_RENAMES_KEY, JSON.stringify(overrides));
}
export function setCategoryLabelOverride(categoryId: string, label: string): void {
  const overrides = loadCategoryLabelOverrides();
  if (label) overrides[categoryId] = label;
  else delete overrides[categoryId];
  saveCategoryLabelOverrides(overrides);
}

export const catalog: DeviceModel[] = [
  // APPLE iPHONES (prices are BASE YUAN)
  { name: "iPhone 11", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "64GB", price: 1450 }, { label: "128GB", price: 1700 }, { label: "256GB", price: 2050 }
  ]},
  { name: "iPhone 11 Pro", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "64GB", price: 1900 }, { label: "256GB", price: 2200 }, { label: "512GB", price: 2550 }
  ]},
  { name: "iPhone 11 Pro Max", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "64GB", price: 2100 }, { label: "256GB", price: 2450 }, { label: "512GB", price: 2750 }
  ]},
  { name: "iPhone 12 mini", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "64GB", price: 1780 }, { label: "128GB", price: 2050 }, { label: "256GB", price: 2350 }
  ]},
  { name: "iPhone 12", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "64GB", price: 2050 }, { label: "128GB", price: 2250 }, { label: "256GB", price: 2550 }
  ]},
  { name: "iPhone 12 Pro", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 2450 }, { label: "256GB", price: 2750 }, { label: "512GB", price: 3050 }
  ]},
  { name: "iPhone 12 Pro Max", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 2750 }, { label: "256GB", price: 3050 }, { label: "512GB", price: 3400 }
  ]},
  { name: "iPhone 13 mini", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 2350 }, { label: "256GB", price: 2650 }, { label: "512GB", price: 2980 }
  ]},
  { name: "iPhone 13", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 2650 }, { label: "256GB", price: 2900 }, { label: "512GB", price: 3200 }
  ]},
  { name: "iPhone 13 Pro", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 3050 }, { label: "256GB", price: 3320 }, { label: "512GB", price: 3600 }, { label: "1TB", price: 4050 }
  ]},
  { name: "iPhone 13 Pro Max", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 3400 }, { label: "256GB", price: 3650 }, { label: "512GB", price: 4050 }, { label: "1TB", price: 4480 }
  ]},
  { name: "iPhone 14", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 3200 }, { label: "256GB", price: 3500 }, { label: "512GB", price: 3850 }
  ]},
  { name: "iPhone 14 Plus", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 3500 }, { label: "256GB", price: 3850 }, { label: "512GB", price: 4180 }
  ]},
  { name: "iPhone 14 Pro", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 4050 }, { label: "256GB", price: 4350 }, { label: "512GB", price: 4700 }, { label: "1TB", price: 5350 }
  ]},
  { name: "iPhone 14 Pro Max", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 4480 }, { label: "256GB", price: 4780 }, { label: "512GB", price: 5120 }, { label: "1TB", price: 5780 }
  ]},
  { name: "iPhone 15", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 3850 }, { label: "256GB", price: 4180 }, { label: "512GB", price: 4600 }
  ]},
  { name: "iPhone 15 Plus", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 4180 }, { label: "256GB", price: 4520 }, { label: "512GB", price: 4950 }
  ]},
  { name: "iPhone 15 Pro", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 4900 }, { label: "256GB", price: 5200 }, { label: "512GB", price: 5650 }, { label: "1TB", price: 6350 }
  ]},
  { name: "iPhone 15 Pro Max", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "256GB", price: 5780 }, { label: "512GB", price: 6200 }, { label: "1TB", price: 6850 }
  ]},
  { name: "iPhone 16", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 4480 }, { label: "256GB", price: 4780 }, { label: "512GB", price: 5200 }
  ]},
  { name: "iPhone 16 Plus", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 4900 }, { label: "256GB", price: 5200 }, { label: "512GB", price: 5650 }
  ]},
  { name: "iPhone 16 Pro", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 5550 }, { label: "256GB", price: 5900 }, { label: "512GB", price: 6350 }, { label: "1TB", price: 7080 }
  ]},
  { name: "iPhone 16 Pro Max", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "256GB", price: 6420 }, { label: "512GB", price: 6850 }, { label: "1TB", price: 7620 }
  ]},
  { name: "iPhone 16e", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 3630 }, { label: "256GB", price: 3930 }
  ]},
  { name: "iPhone 17", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB", price: 5120 }, { label: "256GB", price: 5470 }, { label: "512GB", price: 5900 }
  ]},
  { name: "iPhone 17 Air", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "256GB", price: 5780 }, { label: "512GB", price: 6200 }
  ]},
  { name: "iPhone 17 Pro", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "256GB", price: 6630 }, { label: "512GB", price: 7060 }, { label: "1TB", price: 7700 }
  ]},
  { name: "iPhone 17 Pro Max", category: "iphone", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "256GB", price: 7270 }, { label: "512GB", price: 7700 }, { label: "1TB", price: 8560 }
  ]},

  // SAMSUNG GALAXY S SERIES (prices in GH₵)
  { name: "Samsung Galaxy S21", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM", price: 4500 }, { label: "256GB / 8GB RAM", price: 5200 }
  ]},
  { name: "Samsung Galaxy S21+", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM", price: 5200 }, { label: "256GB / 8GB RAM", price: 5800 }
  ]},
  { name: "Samsung Galaxy S21 Ultra", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 12GB RAM", price: 6500 }, { label: "256GB / 12GB RAM", price: 7200 }, { label: "512GB / 16GB RAM", price: 8000 }
  ]},
  { name: "Samsung Galaxy S21 FE", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 6GB RAM", price: 3800 }, { label: "256GB / 8GB RAM", price: 4500 }
  ]},
  { name: "Samsung Galaxy S22", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM", price: 5500 }, { label: "256GB / 8GB RAM", price: 6200 }
  ]},
  { name: "Samsung Galaxy S22+", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM", price: 6200 }, { label: "256GB / 8GB RAM", price: 6800 }
  ]},
  { name: "Samsung Galaxy S22 Ultra", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM", price: 7500 }, { label: "256GB / 12GB RAM", price: 8200 }, { label: "512GB / 12GB RAM", price: 9000 }, { label: "1TB / 12GB RAM", price: 10000 }
  ]},
  { name: "Samsung Galaxy S23", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM", price: 6500 }, { label: "256GB / 8GB RAM", price: 7200 }
  ]},
  { name: "Samsung Galaxy S23+", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "256GB / 8GB RAM", price: 8000 }, { label: "512GB / 8GB RAM", price: 8800 }
  ]},
  { name: "Samsung Galaxy S23 Ultra", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "256GB / 12GB RAM", price: 9500 }, { label: "512GB / 12GB RAM", price: 10500 }, { label: "1TB / 12GB RAM", price: 11500 }
  ]},
  { name: "Samsung Galaxy S23 FE", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM", price: 5500 }, { label: "256GB / 8GB RAM", price: 6200 }
  ]},
  { name: "Samsung Galaxy S24", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM", price: 7500 }, { label: "256GB / 8GB RAM", price: 8200 }
  ]},
  { name: "Samsung Galaxy S24+", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "256GB / 12GB RAM", price: 9200 }, { label: "512GB / 12GB RAM", price: 10000 }
  ]},
  { name: "Samsung Galaxy S24 Ultra", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "256GB / 12GB RAM", price: 11000 }, { label: "512GB / 12GB RAM", price: 12000 }, { label: "1TB / 12GB RAM", price: 13500 }
  ]},
  { name: "Samsung Galaxy S24 FE", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM", price: 6200 }, { label: "256GB / 8GB RAM", price: 7000 }
  ]},
  { name: "Samsung Galaxy S25", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 12GB RAM", price: 8500 }, { label: "256GB / 12GB RAM", price: 9200 }
  ]},
  { name: "Samsung Galaxy S25+", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "256GB / 12GB RAM", price: 10500 }, { label: "512GB / 12GB RAM", price: 11500 }
  ]},
  { name: "Samsung Galaxy S25 Ultra", category: "samsung", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "256GB / 12GB RAM", price: 12500 }, { label: "512GB / 12GB RAM", price: 13500 }, { label: "1TB / 16GB RAM", price: 15500 }
  ]},

  // APPLE WATCHES (prices are BASE YUAN)
  { name: "Apple Watch Series 3 (GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "38mm", price: 640 }, { label: "42mm", price: 725 }
  ]},
  { name: "Apple Watch Series 3 (GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "38mm", price: 770 }, { label: "42mm", price: 855 }
  ]},
  { name: "Apple Watch SE (1st Gen, GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "40mm", price: 940 }, { label: "44mm", price: 1070 }
  ]},
  { name: "Apple Watch SE (1st Gen, GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "40mm", price: 1110 }, { label: "44mm", price: 1240 }
  ]},
  { name: "Apple Watch SE (2nd Gen, GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "40mm", price: 1200 }, { label: "44mm", price: 1325 }
  ]},
  { name: "Apple Watch SE (2nd Gen, GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "40mm", price: 1370 }, { label: "44mm", price: 1500 }
  ]},
  { name: "Apple Watch Series 4 (GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "40mm", price: 855 }, { label: "44mm", price: 985 }
  ]},
  { name: "Apple Watch Series 4 (GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "40mm", price: 1025 }, { label: "44mm", price: 1155 }
  ]},
  { name: "Apple Watch Series 5 (GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "40mm", price: 1070 }, { label: "44mm", price: 1200 }
  ]},
  { name: "Apple Watch Series 5 (GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "40mm", price: 1240 }, { label: "44mm", price: 1370 }
  ]},
  { name: "Apple Watch Series 6 (GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "40mm", price: 1280 }, { label: "44mm", price: 1410 }
  ]},
  { name: "Apple Watch Series 6 (GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "40mm", price: 1500 }, { label: "44mm", price: 1625 }
  ]},
  { name: "Apple Watch Series 7 (GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "41mm", price: 1500 }, { label: "45mm", price: 1625 }
  ]},
  { name: "Apple Watch Series 7 (GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "41mm", price: 1710 }, { label: "45mm", price: 1840 }
  ]},
  { name: "Apple Watch Series 8 (GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "41mm", price: 1710 }, { label: "45mm", price: 1840 }
  ]},
  { name: "Apple Watch Series 8 (GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "41mm", price: 1925 }, { label: "45mm", price: 2050 }
  ]},
  { name: "Apple Watch Series 9 (GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "41mm", price: 1925 }, { label: "45mm", price: 2050 }
  ]},
  { name: "Apple Watch Series 9 (GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "41mm", price: 2140 }, { label: "45mm", price: 2265 }
  ]},
  { name: "Apple Watch Series 10 (GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "42mm", price: 2140 }, { label: "46mm", price: 2265 }
  ]},
  { name: "Apple Watch Series 10 (GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "42mm", price: 2350 }, { label: "46mm", price: 2480 }
  ]},
  { name: "Apple Watch Series 11 (GPS)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "42mm", price: 2350 }, { label: "46mm", price: 2480 }
  ]},
  { name: "Apple Watch Series 11 (GPS + Cellular)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "42mm", price: 2570 }, { label: "46mm", price: 2695 }
  ]},
  { name: "Apple Watch Ultra (1st Gen)", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "49mm", price: 3210 }
  ]},
  { name: "Apple Watch Ultra 2", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "49mm", price: 3635 }
  ]},
  { name: "Apple Watch Ultra 3", category: "apple-watch", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "49mm", price: 4060 }
  ]},

  // SAMSUNG WATCHES (prices in GH₵)
  { name: "Samsung Galaxy Watch 4 (40mm)", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "40mm", price: 1800 }
  ]},
  { name: "Samsung Galaxy Watch 4 (44mm)", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "44mm", price: 2000 }
  ]},
  { name: "Samsung Galaxy Watch 5 (40mm)", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "40mm", price: 2200 }
  ]},
  { name: "Samsung Galaxy Watch 5 (44mm)", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "44mm", price: 2500 }
  ]},
  { name: "Samsung Galaxy Watch 5 Pro", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "45mm", price: 3200 }
  ]},
  { name: "Samsung Galaxy Watch 6 (40mm)", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "40mm", price: 2800 }
  ]},
  { name: "Samsung Galaxy Watch 6 (44mm)", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "44mm", price: 3000 }
  ]},
  { name: "Samsung Galaxy Watch 6 Classic (43mm)", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "43mm", price: 3500 }
  ]},
  { name: "Samsung Galaxy Watch 6 Classic (47mm)", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "47mm", price: 3800 }
  ]},
  { name: "Samsung Galaxy Watch 7 (40mm)", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "40mm", price: 3200 }
  ]},
  { name: "Samsung Galaxy Watch 7 (44mm)", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "44mm", price: 3500 }
  ]},
  { name: "Samsung Galaxy Watch Ultra", category: "samsung-watch", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "47mm", price: 5500 }
  ]},

  // PIXEL WATCHES (prices in GH₵)
  { name: "Google Pixel Watch", category: "pixel-watch", brand: "Google", usesYuanPricing: false, variants: [
    { label: "41mm WiFi", price: 2500 }, { label: "41mm LTE", price: 3000 }
  ]},
  { name: "Google Pixel Watch 2", category: "pixel-watch", brand: "Google", usesYuanPricing: false, variants: [
    { label: "41mm WiFi", price: 3000 }, { label: "41mm LTE", price: 3500 }
  ]},
  { name: "Google Pixel Watch 3", category: "pixel-watch", brand: "Google", usesYuanPricing: false, variants: [
    { label: "41mm WiFi", price: 3500 }, { label: "41mm LTE", price: 4000 }, { label: "45mm WiFi", price: 4000 }, { label: "45mm LTE", price: 4500 }
  ]},

  // iPADS (prices are BASE YUAN)
  { name: "iPad 9th Gen", category: "ipad", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "64GB WiFi", price: 1710 }, { label: "256GB WiFi", price: 2265 }
  ]},
  { name: "iPad 10th Gen", category: "ipad", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "64GB WiFi", price: 2265 }, { label: "256GB WiFi", price: 2905 }
  ]},
  { name: "iPad Air M1", category: "ipad", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "64GB WiFi", price: 2905 }, { label: "256GB WiFi", price: 3420 }
  ]},
  { name: "iPad Air M2 11\"", category: "ipad", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB WiFi", price: 3420 }, { label: "256GB WiFi", price: 3850 }, { label: "512GB WiFi", price: 4490 }, { label: "1TB WiFi", price: 5130 }
  ]},
  { name: "iPad Air M2 13\"", category: "ipad", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB WiFi", price: 4490 }, { label: "256GB WiFi", price: 4920 }, { label: "512GB WiFi", price: 5560 }, { label: "1TB WiFi", price: 6200 }
  ]},
  { name: "iPad Pro M4 11\"", category: "ipad", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "256GB WiFi", price: 5560 }, { label: "512GB WiFi", price: 6200 }, { label: "1TB WiFi", price: 7480 }, { label: "2TB WiFi", price: 8760 }
  ]},
  { name: "iPad Pro M4 13\"", category: "ipad", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "256GB WiFi", price: 7050 }, { label: "512GB WiFi", price: 7690 }, { label: "1TB WiFi", price: 8970 }, { label: "2TB WiFi", price: 10250 }
  ]},
  { name: "iPad mini 6", category: "ipad", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "64GB WiFi", price: 2565 }, { label: "256GB WiFi", price: 3200 }
  ]},
  { name: "iPad mini 7", category: "ipad", brand: "Apple", usesYuanPricing: true, variants: [
    { label: "128GB WiFi", price: 3200 }, { label: "256GB WiFi", price: 3635 }, { label: "512GB WiFi", price: 4490 }
  ]},

  // SAMSUNG TABLETS (prices in GH₵)
  { name: "Samsung Galaxy Tab S8", category: "android-tablet", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM WiFi", price: 5500 }, { label: "256GB / 8GB RAM WiFi", price: 6200 }
  ]},
  { name: "Samsung Galaxy Tab S8+", category: "android-tablet", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM WiFi", price: 7000 }, { label: "256GB / 8GB RAM WiFi", price: 7800 }
  ]},
  { name: "Samsung Galaxy Tab S8 Ultra", category: "android-tablet", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM WiFi", price: 9000 }, { label: "256GB / 12GB RAM WiFi", price: 10000 }, { label: "512GB / 16GB RAM WiFi", price: 12000 }
  ]},
  { name: "Samsung Galaxy Tab S9", category: "android-tablet", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 8GB RAM WiFi", price: 6500 }, { label: "256GB / 12GB RAM WiFi", price: 7500 }
  ]},
  { name: "Samsung Galaxy Tab S9+", category: "android-tablet", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "256GB / 12GB RAM WiFi", price: 8500 }, { label: "512GB / 12GB RAM WiFi", price: 9500 }
  ]},
  { name: "Samsung Galaxy Tab S9 Ultra", category: "android-tablet", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "256GB / 12GB RAM WiFi", price: 10500 }, { label: "512GB / 12GB RAM WiFi", price: 12000 }, { label: "1TB / 16GB RAM WiFi", price: 14000 }
  ]},
  { name: "Samsung Galaxy Tab S9 FE", category: "android-tablet", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "128GB / 6GB RAM WiFi", price: 4500 }, { label: "256GB / 8GB RAM WiFi", price: 5200 }
  ]},
  { name: "Samsung Galaxy Tab S10+", category: "android-tablet", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "256GB / 12GB RAM WiFi", price: 9500 }, { label: "512GB / 12GB RAM WiFi", price: 10500 }
  ]},
  { name: "Samsung Galaxy Tab S10 Ultra", category: "android-tablet", brand: "Samsung", usesYuanPricing: false, variants: [
    { label: "256GB / 12GB RAM WiFi", price: 11500 }, { label: "512GB / 12GB RAM WiFi", price: 13000 }, { label: "1TB / 16GB RAM WiFi", price: 15000 }
  ]},

  // ACCESSORIES (prices in GH₵, "none" variant config — single variant auto-selected)
  { name: "Phone Case (Clear TPU)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 50 }
  ]},
  { name: "Phone Case (Leather)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 120 }
  ]},
  { name: "Phone Case (Rugged Armor)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 90 }
  ]},
  { name: "USB-C Fast Charger (20W)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 80 }
  ]},
  { name: "USB-C Fast Charger (45W)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 150 }
  ]},
  { name: "Wireless Charger (15W)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 130 }
  ]},
  { name: "Tempered Glass Screen Protector", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 35 }
  ]},
  { name: "Privacy Screen Protector", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 60 }
  ]},
  { name: "Lightning Cable (1m)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 45 }
  ]},
  { name: "USB-C Cable (1m)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 40 }
  ]},
  { name: "Car Phone Mount", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 70 }
  ]},
  { name: "AirPods Pro Case Cover", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 30 }
  ]},
  { name: "Wireless Earbuds (TWS)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 180 }
  ]},
  { name: "Bluetooth Neckband Earphones", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 100 }
  ]},
  { name: "Wired Earbuds (USB-C)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 55 }
  ]},
  { name: "Power Bank (10,000mAh)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 200 }
  ]},
  { name: "Power Bank (20,000mAh)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 350 }
  ]},
  { name: "Power Bank (5,000mAh Slim)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 120 }
  ]},
  { name: "Desktop Phone Stand", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 45 }
  ]},
  { name: "Adjustable Phone Stand (Foldable)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 65 }
  ]},
  { name: "Ring Holder & Kickstand", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 25 }
  ]},
  { name: "Phone Lanyard Strap", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 20 }
  ]},
  { name: "SIM Card Ejector Tool", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 5 }
  ]},

  // TABLET ACCESSORIES (prices in GH₵)
  { name: "Universal Stylus Pen (Passive)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 45 }
  ]},
  { name: "Active Stylus Pen (Palm Rejection)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 250 }
  ]},
  { name: "Apple Pencil (2nd Gen) Compatible Stylus", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 180 }
  ]},
  { name: "S Pen Replacement (Samsung)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 150 }
  ]},
  { name: "Keyboard Case (iPad 10.9\")", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 350 }
  ]},
  { name: "Keyboard Case (iPad Pro 11\")", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 450 }
  ]},
  { name: "Keyboard Case (iPad Pro 13\")", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 500 }
  ]},
  { name: "Keyboard Case (Samsung Tab S9)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 380 }
  ]},
  { name: "Bluetooth Keyboard (Universal)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 200 }
  ]},
  { name: "Tablet Stand (Adjustable Aluminum)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 120 }
  ]},
  { name: "Tablet Stand (Foldable Portable)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 65 }
  ]},
  { name: "Tablet Floor Stand (Gooseneck)", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 180 }
  ]},
  { name: "Tablet Wall Mount", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 95 }
  ]},
  { name: "Tablet Sleeve (10-11\")", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 55 }
  ]},
  { name: "Tablet Sleeve (12-13\")", category: "accessories", brand: "Accessories", usesYuanPricing: false, variants: [
    { label: "Standard", price: 70 }
  ]},
];
